import styled from '@emotion/styled';
import { IMatch } from 'domain/entities/Match/Match';
import { UserIcon } from 'ui/components/elements/UserIcon/UserIcon';
import { CHAT_TYPE } from 'domain/entities/ChatRoom/ChatRoom';
import IconPerson from 'ui/images/icons/person.svg';
import LinkIcon from 'ui/images/link.svg';
import { Link } from 'react-router-dom';
import { mqUser } from 'utils/responsive';
import { ColorLabel } from 'ui/components/elements/ColorLabel/ColorLabel';
import { AGENT_TYPES } from 'domain/entities/Agent/Agent';

import dayjs from 'dayjs';

interface Props {
    match: IMatch;
}

const Wrapper = styled.div<{ isPremium: boolean }>`
    width: 100%;
    border-radius: 8px;
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
    background: ${(props) => (props.isPremium ? 'linear-gradient(to right, #fff8e1, #ffeb99)' : 'var(--white)')};
    padding: 20px;
    margin: 0 auto;
    position: relative;

    ${mqUser('SP')} {
        padding: 16px 12px;
    }
`;

const ImageWrapper = styled.div<{ noImg: boolean }>`
    width: 140px;
    height: 140px;
    border-radius: 10px;
    background-color: #808080;
    position: relative;
    overflow: hidden;
    cursor: pointer;

    :hover {
        opacity: 0.8;
    }

    img {
        width: ${(props) => (props.noImg ? '66%' : '100%')};
        height: ${(props) => (props.noImg ? '66%' : '100%')};
        object-fit: ${(props) => (props.noImg ? 'contain' : 'cover')};
        object-position: center;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
    }

    ${mqUser('SP')} {
        width: 100%;
        height: auto;
        max-height: 200px;
        aspect-ratio: 4 / 3;
    }
`;

const Flex = styled.div`
    display: flex;
    gap: 20px;

    ${mqUser('SP')} {
        flex-direction: column;
        gap: 12px;
    }
`;

const FlexCenter = styled.div`
    display: flex;
    gap: 13px;
    align-items: center;
`;

const FlexColumn = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const Title = styled.div`
    font-size: var(--font-large);
    font-weight: var(--font-bold);
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 1px;
    :hover {
        opacity: 0.8;
    }

    img {
        width: 16px;
        height: 16px;
    }
`;

const Detail = styled.div`
    font-size: var(--font-medium);
    font-weight: var(--font-bold);
`;

const Label = styled.div`
    color: var(--white);
    background: linear-gradient(45deg, #daa520, #b8860b);
    padding: 0.2rem 0.9rem;
    border-radius: 4px;
    font-size: var(--font-medium);
    font-weight: 600;
    width: fit-content;
    height: fit-content;
`;

const ButtonContainer = styled.div`
    display: flex;
    gap: 20px;
    justify-content: flex-end;
    position: absolute;
    right: 50px;
    bottom: 20px;

    ${mqUser('SP')} {
        position: static;
        justify-content: flex-start;
        flex-direction: column;
        gap: 12px;
        width: 100%;
        margin-top: 20px;
    }
`;

const Button = styled.button`
    background-color: var(--main-color-blue);
    color: var(--white);
    border: none;
    font-weight: 700;
    font-size: var(--font-large);
    border-radius: 5px;
    padding: 8px 10px;
    cursor: pointer;

    :hover {
        opacity: 0.8;
    }

    ${mqUser('SP')} {
        width: 100%;
        font-size: var(--font-medium);
    }
`;

const SurveyButton = styled(Button)`
    background-color: var(--primary);
`;

const AgentNameContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 20px;
    font-weight: var(--font-bold);
    font-size: var(--font-x-large);

    ${mqUser('SP')} {
        font-size: var(--font-medium);
        flex-direction: column;
        align-items: flex-start;
    }
`;

const LabelsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
`;

export const MatchCard: React.FC<Props> = ({ match }) => {
    if (match.chat_room && match.chat_room?.chat_type === CHAT_TYPE.CHAT_TYPE_SUPPORT) {
        // サポートチャットは表示しない
        return null;
    }

    const imgURL = match.agent.icon_image;
    const agentObj = match.agent;

    const today = dayjs();

    return (
        <Wrapper isPremium={match.premium_offer}>
            <Flex>
                <ImageWrapper
                    noImg={!imgURL}
                    onClick={() => {
                        window.open(`/buyer/agent_profile/${agentObj.id}/`, '_blank');
                    }}
                >
                    <img src={imgURL || IconPerson} alt={'サムネイル'} />
                </ImageWrapper>
                <FlexColumn>
                    <FlexCenter>
                        <Title
                            onClick={() => {
                                window.open(`/buyer/agent_profile/${agentObj.id}/`, '_blank');
                            }}
                        >
                            {agentObj.name}
                            <img src={LinkIcon} alt={'リンクアイコン'} />
                        </Title>
                        <LabelsContainer>
                            {agentObj.agent_type === AGENT_TYPES.AGENCY.value && (
                                <ColorLabel color="#ee7800" backgroundColor="#fafad2" borderColor="#ee7800">
                                    {AGENT_TYPES.AGENCY.label}
                                </ColorLabel>
                            )}
                            {agentObj.agent_type === AGENT_TYPES.FA.value && (
                                <ColorLabel color="#006eb0" backgroundColor="#e5ffff" borderColor="#006eb0">
                                    {AGENT_TYPES.FA.label}
                                </ColorLabel>
                            )}
                            {agentObj.agent_type === AGENT_TYPES.BUSINESS_ACQUISITION.value && (
                                <ColorLabel color="#56256e" backgroundColor="#efefff" borderColor="#56256e">
                                    {AGENT_TYPES.BUSINESS_ACQUISITION.label}
                                </ColorLabel>
                            )}
                            {agentObj.is_sole_proprietor && (
                                <ColorLabel color="#008000" backgroundColor="#d3ffa8" borderColor="#008000">
                                    個人事業主
                                </ColorLabel>
                            )}
                            {match.premium_offer && <Label color={'#b8860b'}>プレミアムオファー</Label>}
                        </LabelsContainer>
                    </FlexCenter>
                    <AgentNameContainer>
                        <UserIcon size={30} src={match.from_agent_user.icon_image} />
                        <span>{`${match.from_agent_user.last_name} ${match.from_agent_user.first_name} 様`}</span>
                    </AgentNameContainer>

                    <Detail>
                        面談日：{match.meeting_date ? dayjs(match.meeting_date).format('YYYY/MM/DD') : '未設定'}
                    </Detail>
                </FlexColumn>
                <ButtonContainer>
                    {!match?.already_survey_answered && match.meeting_date && today.isAfter(match.meeting_date) && (
                        <Link to={`/buyer/survey/form/${match.id}/`}>
                            <SurveyButton>アンケート回答</SurveyButton>
                        </Link>
                    )}
                    <Link to={`/buyer/message/${match.chat_room?.id}/`}>
                        <Button>チャットへ</Button>
                    </Link>
                </ButtonContainer>
            </Flex>
        </Wrapper>
    );
};
